import UploadIcon from 'components/icons/upload-icon/index.vue'
import AnimationProcessing from 'components/animations/processing'
import { UploadObject } from 'src/utils/objects/uploader'
import MethodsGraphics from 'src/pages/submissions/components/graphics/script/methods/graphics'
import { mapGetters } from 'vuex'
import Errors from 'src/utils/helper/errors'
import { calculateMD5, createImagePreSignedURL } from 'src/utils/helper/misc'
import i18n from 'src/assets/i18n/i18n'

const { t } = i18n().global
export default {
  name: 'graphic-uploader',

  components: {
    UploadIcon,
    AnimationProcessing
  },

  props: {
    sortOrder: {
      type: Object,
      required: true
    },
    limit: {
      type: [String, Number],
      default: 15
    },
    format:{
      type:String,
      default:'png, jpg, jpeg' 
    },
    maxSize: {
      type: Number,
      default: 8388608
    },
  },

  data() {
    return {
      uploader: UploadObject()
    }
  },

  computed: {
    ...mapGetters('graphics', {
      graphicsList: ['list'],
      graphicsStates: ['states'],
      uploading: ['uploading']
    })
  },

  methods: {
    /**
     * Set uploader on target.
     * Pass handle to dispatch submits to binaries/upload in $store.
     * Results will be proceeded in results.vue.
     */
    async setUploader() {
      const $0 = this;
      if (this.$el.querySelector('#qq-graphics-uploader-zone')) {
        this.uploader.create({
          context: this,
          target: '#qq-graphics-uploader-zone',
          handle: async function () {
            const submits = [];
            const limit = $0.limit * 1;
            const uploads = $0.uploader.getSubmits();
            const validImages = await $0.validateErrors(uploads)
            if (validImages.length === 0) return;
            const alredyUploadedImgMd5 = $0.graphicsList.map((item) => item.image.md5Checksum)
            
            const submissionId = $0.$store.getters['submissions/submission'].id;
            const createPresignedImagesArray = await createImagePreSignedURL(validImages, alredyUploadedImgMd5, submissionId, true)
            const md5Promises = createPresignedImagesArray.map((upload, idx) => {
              if ($0.sortOrder.graphics.length >= limit) {
                $0.showError();
                return Promise.resolve(null); // Skip this upload
              }
              const item = MethodsGraphics.createGraphic({
                status: $0.graphicsStates.UPLOADED
              });
              $0.sortOrder.graphics.push(item);
              return calculateMD5(upload.file)
                .then((md5) => {
                  submits.push({
                    imageType: 'Poster',
                    channel: 'myfonts',
                    graphicId: item.graphicId,
                    md5Checksum: md5,
                    fileName: upload.file.name,
                    fileType: upload.file.type,
                    fileSize: upload.file.size,
                  });
                });
            });
    
            Promise.all(md5Promises)
              .then(() => {
                /**
                 * Tell Expand to re-calculate.
                 */
                $0.$events.$emit('fou:expand:update');
    
                /**
                 * If uploads push to API.
                 */
                if (submits.length > 0) {
                  $0.$store.dispatch('graphics/upload', {
                    submissionId: $0.$store.getters['submissions/submission'].id,
                    submits
                  });
                }
              })
              .catch(error => {
                console.error('Error calculating MD5:', error);
              });
          }
        });
      }
    },
    showError() {
      const err = Errors.create({
        message: this.$t('pages.submissions.components.graphics.poster.upload_limit_exceeded')
      })
      this.$store.dispatch('errors/throw', err)
    },
    async validateErrors(images) {
      const maxImageSize = process.env.MAX_IMAGE_SIZE
      const minImageWidth = process.env.MIN_IMAGE_WIDTH
      const minImageHeight = process.env.MIN_IMAGE_HEIGHT
      const validImages = []
      for (const file of images) {
        if (!file.file.type.startsWith('image/')) {
          const customError = { message: 'We accept only PNG, JPG & JPEG files.' };
          const err = Errors.create(customError);
          this.$store.commit('errors/add', err);
          continue;  // Skip to the next file
        }
      
        const allowedFormats = ['png', 'jpg', 'jpeg'];
        const isFileFormatInvalid = !allowedFormats.some(format => file.file.type.includes(format));
        if (isFileFormatInvalid) {
          const customError = { message: 'We accept only PNG, JPG & JPEG files.' };
          const err = Errors.create(customError);
          this.$store.commit('errors/add', err);
          continue;  // Skip to the next file
        }
          
        const validateImageRatio = (file) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = URL.createObjectURL(file.file);
              
            img.onload = () => {
              if (img.width < minImageWidth || img.height < minImageHeight || img.width / img.height !== 2) {
                const customError = { message: `Incorrect image size, please upload the correct image size as per the specifications.` };
                const err = Errors.create(customError);
                this.$store.commit('errors/add', err);
                reject(new Error('Reject if the validation fails'))
                }
              else if (file.file.size > maxImageSize) {
                const customError = { message: t('pages.inventory_ingestion_hub.image_drop_zone.check_image_size', {
                  fileName: file.file.name,
                  })};
                const err = Errors.create(customError);
                this.$store.commit('errors/add', err);
                reject(new Error('Reject if the validation fails'))
              }
              else {
                resolve(true);  // Resolve if the validation passes
              }
            };
      
            img.onerror = () => {
              reject(new Error('Handle error case'))
            };
          });
        };
          
        const handleImageValidation = async (file) => {
          try {
            const isValid = await validateImageRatio(file);
            if (isValid) {
              return true;
            }
          } catch (error) {
            return false;
          }
        };
          
        const validationData = await handleImageValidation(file);
        if (!validationData) continue;  // Skip if validation fails
          
        validImages.push(file);
      }
    // }
      
      // Call the function with images array
      // processImages(images);
      
      return validImages
    },
  },

  mounted() {
   this.setUploader()
  },

  updated() {
    /*
     * Make sure the uploader is reset in Template after render update.
     */
    this.setUploader()
  }
}
